import * as ReactDOM from 'react-dom';
import { createAppContext, ConfigSource, IConfigSource, ICreateAppContextOptions } from '@agilepoint/wat';
import { combineUrl, memoizeFunction } from '@agilepoint/utils-common';
import './themes';
import { IApplicationConfig } from './types';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { LoginPage } from 'modules/login/pages/login-page';
import { IAgilePointConfiguration } from 'modules/login/types';
import { getCookie } from 'common';
import { DEFAULT_COOKIE_EXPIRATION_VALUE } from 'modules/login/hooks';
import { AppInitializer } from 'app-initializer';

declare global {
    // tslint:disable-next-line:interface-name
    interface Window {
        __AP__: {
            authentication?: ICreateAppContextOptions['authentication'];
            agilepoint: IAgilePointConfiguration,
            mobileAppAccelerator?: {
                config: IApplicationConfig;
            }
        };
    }
}

export const COOKIE_NAME = 'MAA_AP_Auth';

const configFilePath = combineUrl(process.env.PUBLIC_URL, `config.json?${new Date()}`);

const getCookieExpirationVal = memoizeFunction((value: number | undefined): number => {
    const defaultCookieExpiration = 3600;
    try {
        if (!value) {
            return defaultCookieExpiration;
        }
        const date = new Date();
        // here days are converted to milliseconds
        date.setTime(date.getTime() + ((value || DEFAULT_COOKIE_EXPIRATION_VALUE) * 24 * 60 * 60 * 1000));
        return Number(date.toUTCString());
    }
    catch (err) {
        return defaultCookieExpiration;
    }
});

export default async function boot() {
    const cookieValue = getCookie(COOKIE_NAME);
    const configValue = cookieValue && JSON.parse(cookieValue || '');

    const configSource: IConfigSource<IApplicationConfig> = configValue ?
        ConfigSource.from(configValue) :
        ConfigSource.fromFile<IApplicationConfig>(configFilePath);

    async function getAuthInfo() {
        return { auth: configValue?.agilepoint?.auth, expires: getCookieExpirationVal(configValue?.agilepoint?.cookieExpirationDays) };
    }

    const appContext = await createAppContext<IApplicationConfig>({
        configSource, authentication: {
            getToken: getAuthInfo,
            renewToken: getAuthInfo
        }
    });

    const { rootSelector = '#mobile-app-accelerator-root' } = appContext.configuration;

    const host = document.querySelector(rootSelector);

    if (!host) {
        throw new Error('Invalid host. Please check rootSelector value in your configuration');
    }

    ReactDOM.render(
        <>
            {!configValue ? <LoginPage appContext={appContext} /> : <AppInitializer appContext={appContext} />}
        </>, host);
}

boot();

serviceWorkerRegistration.unregister();

